import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  MeetingRoom as RoomIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getRoomsByType, updateRoomInFacility } from '../../services/operatingRoomService';
import { useUserData } from '../../hooks/useUserData';
import { DatePicker } from '@mui/x-date-pickers';
import { RoomType } from '../../types/enums';

const OperatingRoomManagement = () => {
  const { defaultFacility } = useUserData();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [dialogType, setDialogType] = useState('add'); // 'add', 'edit', 'view'
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load operating rooms from the backend
  const loadOperatingRooms = useCallback(async () => {
    if (!defaultFacility) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get rooms with type "Operating Room"
      const operatingRooms = await getRoomsByType(defaultFacility.id, RoomType.OPERATING_ROOM);
      
      console.log('Raw operating rooms from backend:', operatingRooms);
      
      // Transform the data to match the expected format
      const transformedRooms = operatingRooms.map(room => {
        console.log('Processing room:', room);
        return {
          id: room.id,
          name: room.name,
          status: room.status,
          type: room.specialties?.[0] || 'General', // Use first specialty or default to General
          capacity: room.capacity,
          equipment: room.equipment || [],
          currentCase: null, // This would come from surgical cases in the future
          nextCase: null, // This would come from surgical schedule in the future
          maintenanceDue: room.maintenanceDue,
          lastCleaned: room.lastCleaned,
          temperature: room.temperature,
          humidity: room.humidity,
          airQuality: room.airQuality,
          departmentName: room.departmentName,
          departmentId: room.departmentId, // Add departmentId for updates
          notes: room.notes
        };
      });
      
      setRooms(transformedRooms);
    } catch (err) {
      console.error('Error loading operating rooms:', err);
      setError('Failed to load operating rooms. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility]);

  useEffect(() => {
    loadOperatingRooms();
  }, [loadOperatingRooms]);

  // Helper functions for room status
  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return '#4caf50';
      case 'occupied':
        return '#2196f3';
      case 'maintenance':
        return '#ff9800';
      case 'out_of_service':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircleIcon />;
      case 'occupied':
        return <PersonIcon />;
      case 'maintenance':
        return <BuildIcon />;
      case 'out_of_service':
        return <ErrorIcon />;
      default:
        return <WarningIcon />;
    }
  };

  // Dialog handlers
  const handleOpenDialog = (type, room = null) => {
    setDialogType(type);
    setSelectedRoom(room);
    if (type === 'add') {
      setFormData({
        name: '',
        type: '',
        capacity: '',
        equipment: [],
        maintenanceDue: ''
      });
    } else if (type === 'edit' && room) {
      setFormData({
        name: room.name || '',
        type: room.type || '',
        capacity: room.capacity || '',
        equipment: room.equipment || [],
        maintenanceDue: room.maintenanceDue || ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRoom(null);
    setFormData({
      name: '',
      type: '',
      capacity: '',
      equipment: [],
      maintenanceDue: ''
    });
  };

  const handleSubmit = async () => {
    if (!selectedRoom || !defaultFacility) {
      handleCloseDialog();
      return;
    }

    console.log('handleSubmit - selectedRoom:', selectedRoom);
    console.log('handleSubmit - defaultFacility:', defaultFacility);
    console.log('handleSubmit - selectedRoom.departmentId:', selectedRoom.departmentId);

    try {
      // Update the room in the facilities collection
      await updateRoomInFacility(
        defaultFacility.id,
        selectedRoom.departmentId,
        selectedRoom.id,
        {
          name: formData.name,
          capacity: parseInt(formData.capacity),
          equipment: formData.equipment,
          maintenanceDue: formData.maintenanceDue || null,
          updatedAt: new Date(),
          updatedBy: 'current-user' // This would come from auth context in the future
        }
      );

      // Reload the rooms to get the updated data
      await loadOperatingRooms();
      
      // Show success message (you could add a snackbar here)
      console.log('Room updated successfully');
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error updating room:', error);
      // Show error message (you could add error handling here)
      alert('Failed to update room. Please try again.');
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    capacity: '',
    equipment: [],
    maintenanceDue: ''
  });

  const RoomCard = ({ room }) => (
    <Card sx={{ 
      height: '100%',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: getStatusColor(room.status) }}>
              {getStatusIcon(room.status)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {room.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {room.type} • Capacity: {room.capacity}
                {room.departmentName && ` • ${room.departmentName}`}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={room.status}
            size="small"
            sx={{
              bgcolor: getStatusColor(room.status),
              color: 'white',
              fontWeight: 500,
              textTransform: 'capitalize'
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          {room.currentCase ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Current: {room.currentCase}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Available
              </Typography>
            </Alert>
          )}
          
          {room.nextCase && (
            <Typography variant="body2" color="text.secondary">
              Next: {room.nextCase}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => handleOpenDialog('view', room)}>
                <ViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Room">
              <IconButton size="small" onClick={() => handleOpenDialog('edit', room)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {room.lastCleaned && (
              <Typography variant="caption" color="text.secondary" display="block">
                Last cleaned: {room.lastCleaned}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              ID: {room.id}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Operating Room Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage operating rooms, schedules, and equipment. Data is loaded from facility department structure.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              if (defaultFacility) {
                loadOperatingRooms();
              }
            }}
            sx={{ borderRadius: 2 }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>



      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#4caf50', mb: 1 }}>
              {rooms.filter(r => r.status === 'available').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Available Rooms
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#2196f3', mb: 1 }}>
              {rooms.filter(r => r.status === 'occupied').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Occupied Rooms
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
              {rooms.filter(r => r.status === 'maintenance').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Under Maintenance
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#9c27b0', mb: 1 }}>
              {rooms.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Rooms
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {/* No Rooms State */}
          {rooms.length === 0 && !error ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No operating rooms found for this facility. Make sure rooms are configured with type "Operating Room".
            </Alert>
          ) : (
            /* Room Cards */
            <Grid container spacing={3}>
              {rooms.map((room) => (
                <Grid item xs={12} sm={6} md={4} key={room.id}>
                  <RoomCard room={room} />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RoomIcon />
            {dialogType === 'add' ? 'Add Operating Room' : 
             dialogType === 'edit' ? 'Edit Operating Room' : 'Room Details'}
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {dialogType === 'view' && selectedRoom ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This data is loaded from the facility's department and room structure.
              </Alert>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Room Information</Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Room Name" secondary={selectedRoom.name} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Type" secondary={selectedRoom.type} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Capacity" secondary={selectedRoom.capacity} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Status" secondary={selectedRoom.status} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Equipment</Typography>
                  <List>
                    {selectedRoom.equipment.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Schedule</Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Current Case" 
                        secondary={selectedRoom.currentCase || 'No current case'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Next Case" 
                        secondary={selectedRoom.nextCase} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Maintenance Due" 
                        secondary={selectedRoom.maintenanceDue} 
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Note: New rooms will be added to the facility's department structure.
              </Alert>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Room Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Room Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Room Type"
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <MenuItem value="General">General</MenuItem>
                    <MenuItem value="Cardiac">Cardiac</MenuItem>
                    <MenuItem value="Orthopedic">Orthopedic</MenuItem>
                    <MenuItem value="Neurosurgery">Neurosurgery</MenuItem>
                    <MenuItem value="Ophthalmology">Ophthalmology</MenuItem>
                    <MenuItem value="Pediatric">Pediatric</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Maintenance Due"
                  value={formData.maintenanceDue ? new Date(formData.maintenanceDue) : null}
                  onChange={(date) => setFormData({ ...formData, maintenanceDue: date ? date.toISOString().split('T')[0] : '' })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Equipment (comma-separated)"
                  value={formData.equipment.join(', ')}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    equipment: e.target.value.split(',').map(item => item.trim()) 
                  })}
                  helperText="Enter equipment items separated by commas"
                />
              </Grid>
            </Grid>
            </>
          )}
        </DialogContent>
        {dialogType !== 'view' && (
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {dialogType === 'add' ? 'Add Room' : 'Save Changes'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default OperatingRoomManagement; 